/** A cache for storing computed values based on request keys, per request. */
export class RequestCacheStore {
  private _cache: Map<unknown, unknown> | undefined;

  /**
   * Get the cached value for the given key, or compute and cache it if not present.
   *
   * Key equality is determined by the "SameValueZero" algorithm.
   *
   * @param key - The cache key.
   * @param compute - A function to compute the value if not cached.
   * @returns The cached or computed value.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness#same-value-zero_equality SameValueZero algorithm}
   */
  public getOrInsertComputed<T>(key: unknown, compute: () => T): T {
    this._cache ??= new Map();
    if (!this._cache.has(key)) {
      this._cache.set(key, compute());
    }
    return this._cache.get(key) as T;
  }

  /**
   * Delete the cached value for the given key.
   * @param key - The cache key.
   */
  public delete(key: unknown): void {
    this._cache?.delete(key);
  }

  /** Clear all cached values. */
  public clear(): void {
    this._cache?.clear();
  }
}
